import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../apiClient';
import { GET_SIGNUP_DOCUMENT_QUERY_KEY } from './useGetSignupDocumentUrl';
import { GET_USER_BY_ID_QUERY_KEY } from './useGetUser';
import { GET_USER_MEMBERSHIPS_QUERY_KEY } from './useGetUserMemberships';

type ApproveResponse = { status: 'verified'; userId: string };

export const useApproveSignupDocument = (
    userId: string,
    onSuccess?: () => void,
    onError?: (error: Error) => void,
) => {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['approveSignupDocument', userId],
        mutationFn: () =>
            apiClient.fetchApi<ApproveResponse, {}>({
                method: 'POST',
                endpoint: `users/${userId}/signup-document/approve`,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: GET_USER_BY_ID_QUERY_KEY(userId) });
            // Approve may auto-assign a membership; the panel reads the
            // membership name via useGetUserMemberships, so its cache also
            // needs to be refreshed.
            queryClient.invalidateQueries({ queryKey: GET_USER_MEMBERSHIPS_QUERY_KEY(userId) });
            // The verification result + verifiedAt + status fields are now
            // served from the admin signup-document endpoint, not the user
            // details. Invalidate so the panel reflects the new verdict.
            queryClient.invalidateQueries({ queryKey: GET_SIGNUP_DOCUMENT_QUERY_KEY(userId) });
            onSuccess?.();
        },
        onError,
    });
};
